import type React from "react";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UserProfileProps {
  userName: string;
  userEmail: string;
  userPhoto: string | null;
}

const UserProfile: React.FC<UserProfileProps> = ({
  userName,
  userEmail,
  userPhoto,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = "none";
    const fallback = e.currentTarget.nextElementSibling;
    if (fallback) fallback.classList.remove("hidden");
  };

  const AvatarFallback = () => {
    const initial = userName.charAt(0).toUpperCase();
    return (
      <div className="w-9 h-9 rounded-full bg-zinc-600 flex items-center justify-center text-white font-bold text-sm ring-2 ring-white shadow-sm">
        {initial}
      </div>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("details");
    localStorage.removeItem("_token");
    navigate("/");
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-zinc-100 transition-colors group"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {/* Avatar */}
        {userPhoto ? (
          <img
            src={userPhoto}
            alt="User avatar"
            className="w-9 h-9 rounded-full ring-2 ring-white object-cover shadow-sm"
            onError={handleImageError}
          />
        ) : null}
        <div className={userPhoto ? "hidden" : ""}>
          <AvatarFallback />
        </div>

        {/* Name (desktop only) */}
        <span className="hidden md:block text-sm font-medium text-zinc-900 truncate max-w-[120px]">
          {userName}
        </span>

        {/* Chevron */}
        <ChevronDown
          size={16}
          className={`text-zinc-600 transition-transform group-hover:text-zinc-800 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-zinc-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 z-50">
          {/* User info header (optional, looks nice) */}
          <div className="px-4 py-3 border-b border-zinc-100">
            <p className="text-sm font-semibold text-zinc-900 truncate">
              {userName}
            </p>
            <p className="text-xs text-zinc-500 truncate">{userEmail}</p>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-50 transition-colors text-zinc-700 text-sm font-medium"
          >
            <LogOut size={16} className="text-zinc-600" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
